import { initConsoleCapture } from '@/lib/debug/console-buffer';
initConsoleCapture(1000);

import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import ErrorBoundary from './ErrorHandling/ErrorBoundary';
import ErrorPage from './ErrorHandling/ErrorPage';
import NotFoundPage from './ErrorHandling/NotFoundPage';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import './index.css';
import App from './App';
import Login from './Login/Login';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import Devices from '../features/Devices/Devices';
import { Toaster } from '../components/ui/toaster';
import Home from './Home';
import IntegrationHub from './IntegrationHub/IntegrationHub';
import IntegrationPage from './IntegrationHub/Integration';
import PollingMonitor from './IntegrationHub/PollingMonitor';
import CreatePassword from './CreatePassword/CreatePassword';
import WelcomeGo from './WelcomeGo/WelcomeGo';
import DownloadGo from './WelcomeGo/DownloadGo';
import ForgotPasswordGo from './WelcomeGo/ForgotPasswordGo';
import ResetPasswordGo from './WelcomeGo/ResetPasswordGo';
import SiteDetails from './Sites/Site';
import Sites from './Sites/Sites';
// posthog is now only used in providers.tsx
import { HotkeyProvider, useHotkeyMode } from '@/lib/hotkey-context';
import { initSentry } from '@/lib/telemetry';
import { usePostHog, useFeatureFlagEnabled } from 'posthog-js/react';
import { PHProvider } from '../helpers/providers';
import AddDevice from '../features/Devices/AddDevice/AddDevice';
import DeviceAddNew from '../features/Devices/AddDevice/DeviceAddNew';
import DevicePage from '@/features/Devices/DevicePage/DevicePage';
import { DisconnectedOverlay } from '@/components/DisconnectedOverlay';
import { FeatureFlags } from '@/lib/feature-flag';
import { FeatureFlagged } from '@/components/internal/feature-flagged';

// src/main.js
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('Service Worker registered with scope:', registration.scope);
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
  });
}

const Alert = React.lazy(() => import('./Alert/Alert'));
const AlertAuditPage = React.lazy(() => import('./Alert/AlertAuditPage'));
const AlertLogin = React.lazy(() => import('./Alert/AlertLogin'));
const AlertViewerWithLogin = React.lazy(() => import('./Alert/AlertViewerWithLogin'));

const CustomersPage = React.lazy(() => import('../features/Customers/Customers'));
const CustomerDetailV2 = React.lazy(() => import('../features/Customers/CustomerDetailV2'));

const AddPatrolPage = React.lazy(() => import('@/features/Patrol/AddPatrolPage'));
const PatrolsPage = React.lazy(() => import('@/features/Patrol/PatrolsPage'));

const ProductsPage = React.lazy(() => import('@/features/Products/ProductsPage'));
const SalesPage = React.lazy(() => import('@/features/Sales/SalesPage'));
const BillingPage = React.lazy(() => import('./Billing/BillingPage'));

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '@/styles/react-big-calendar.css';

// useAuthStore is now only used in providers.tsx
import { RequireShift } from './Shift/components/RequireShift';

import { useEffect, useRef } from 'react';
// useLocation imported from react-router-dom at top of file
import Sidecar from '../components/maps/Sidecar';
import { MultiWindowProvider } from '@/components/MultiWindowProvider';

import Header from '@/components/Header';
import Health from '@/features/Devices/Health';
import TwoFactorPage from './Login/TwoFactorPage';
import { UsersPage } from '@/features/Users/UsersPage';
import { EditUsersPageSimple } from '@/features/Users/EditUsersPageSimple';
import { EditUsersPage } from '@/features/Users/EditUsersPage';
import ExternalConnections from '@/features/ExternalConnections/ExternalConnections';
import AddConnection from '@/features/ExternalConnections/AddConnection/AddConnection';
import EditConnection from '@/features/ExternalConnections/EditConnection/EditConnection';
import AdminAlert from '@/features/AdminAlert/AdminAlert';
import { AlertReportPage } from '@/features/AdminAlert/components/AlertReportPage';
import SystemPage from '@/features/System/SystemPage';
import Inbound from '@/features/System/Inbound';
import RoleProtectedRoute from './Roles/RoleProtectedRoute';
import CustomerRouteProtection from './Customer/CustomerRouteProtection';
import { Loader2, Users2Icon } from 'lucide-react';
import { Roles } from './Roles/useRouteAccess';
import SiteAddNew from './Sites/SiteAddNew';
import Teamlist from '@/features/MonitoringTeams/Teamlist';
import AddTeamPage from '@/features/MonitoringTeams/AddTeamPage';
import TeamDetailPage from '@/features/MonitoringTeams/TeamDetailPage';
import { AddUser } from '@/features/Users/components/AddUser';
const MonitoringOverview = React.lazy(
  () => import('../features/MonitoringOverview/MonitoringOverview')
);
const MonitoringOverviewV2 = React.lazy(
  () => import('../features/MonitoringOverview2/MonitoringOverviewPage')
);
const KanbanTVDashboard = React.lazy(() => import('./KanbanTVDashboard/KanbanTVDashboard'));
const ReportingPage = React.lazy(() => import('./Reporting/ReportingPage'));
import AdminAlerts from '@/features/AdminAlert/AdminAlerts';
import OneLink from './Login/OneLink';
import PromoTerms from './PromoTerms/PromoTerms';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AlertLiveViewSidecar } from './Alert/AlertLiveViewSidecar/AlertLiveViewSidecar';
import Viewer2 from '@/features/Viewer/Viewer2';
import { CameraPage } from '@/features/Devices/CameraPage/CameraPage';
import { ShiftBroadcastListener } from '@/lib/shift/shift-broadcast';
import { RedeemTokenPage } from './Login/RedeemTokenPage';
import { IdleShiftTerminator, ShiftTimeoutListeners } from '@/lib/shift/shift-timeout-listeners';
import { ShiftBreachToaster } from '@/lib/shift/shift-breach-toaster';
import { NavigationSheet } from '@/components/NavigationSheet';
import { OnboardingTrigger } from '@/components/onboarding/OnboardingTrigger';
import { OnboardingDevTools } from '@/components/onboarding/OnboardingDevTools';
import PromptTester from '@/features/Testing/PromptTester';
import { FrontChatProvider } from '@/providers/FrontChatProvider';
import { AnnouncementAutoDismiss, WhatsNewModal, WhatsNewPage } from '@/features/announcements';
import { PageTitle } from '@/components/PageTitle';

interface TitledRoute {
  path: string;
  title?: string;
  element: React.ReactElement;
}

function withTitles(routes: TitledRoute[]) {
  return routes.map(({ title, ...route }) => ({
    ...route,
    element: title ? (
      <>
        <PageTitle title={title} />
        {route.element}
      </>
    ) : (
      route.element
    ),
  }));
}

const EdgeLinkPage = React.lazy(() => import('@/features/EdgeLink/EdgeLinkPage'));
const AddEdgeLinkPage = React.lazy(() => import('@/features/EdgeLink/AddEdgeLinkPage'));
const EdgeLinkDetailPage = React.lazy(() => import('@/features/EdgeLink/EdgeLinkDetailPage'));
const EdgeLinkSharedStatusPage = React.lazy(
  () => import('@/features/EdgeLink/SharedStatus/EdgeLinkSharedStatusPage')
);
const EdgeLinkDeploymentsPage = React.lazy(
  () => import('@/features/EdgeLinkDeployments/EdgeLinkDeploymentsPage')
);

const environment = import.meta.env.VITE_ENV;
const shouldInitSentry = environment && environment !== 'local';

if (shouldInitSentry) {
  initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment,
    release: __COMMIT_SHA__,
  });
}

function PostHogPageView() {
  const location = useLocation();
  const ph = usePostHog();
  const prevLocationRef = useRef(location);

  useEffect(() => {
    // Capture page view event when location changes
    ph.capture('$pageview', {
      $current_url: window.location.href,
    });

    // Capture page leave event for the previous page
    if (prevLocationRef.current.pathname !== location.pathname) {
      ph.capture('$pageleave', {
        $previous_url: window.location.origin + prevLocationRef.current.pathname,
        $current_url: window.location.href,
        duration_seconds: getDurationOnPage(),
      });

      // Reset the timer for the new page
      resetPageTimer();
    }

    // Update the previous location
    prevLocationRef.current = location;
  }, [location, ph, prevLocationRef]);

  // Initialize and manage page timer
  const resetPageTimer = () => {
    window.sessionStorage.setItem('pageEnterTime', Date.now().toString());
  };

  const getDurationOnPage = () => {
    const enterTime = window.sessionStorage.getItem('pageEnterTime');
    if (!enterTime) return 0;

    const durationMs = Date.now() - parseInt(enterTime, 10);
    return Math.round(durationMs / 1000); // Convert to seconds
  };

  // Set initial page timer on component mount
  useEffect(() => {
    resetPageTimer();

    // Handle browser/tab close or navigation away from the site
    const handleBeforeUnload = () => {
      ph.capture('$pageleave', {
        $previous_url: window.location.href,
        duration_seconds: getDurationOnPage(),
        exit_type: 'browser_close',
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [ph]);

  return null;
}

// Import PHProvider from helpers/providers.tsx instead of defining it here
// This ensures we have a single source of truth for PostHog initialization

const MonitoringRoutes = () => {
  return (
    <RoleProtectedRoute roles={['Monitoring']}>
      <Header />

      <NavigationSheet />
      <PostHogPageView />
      <OnboardingTrigger />
      <Outlet />
    </RoleProtectedRoute>
  );
};

const MonitoringRoutesNoLayout = () => {
  return (
    <RoleProtectedRoute roles={['Monitoring', Roles.SuperAdmin]}>
      <PostHogPageView />
      <Outlet />
    </RoleProtectedRoute>
  );
};

const AdminRoutes = () => {
  return (
    <RoleProtectedRoute
      roles={[Roles.IntegratorAdmin, Roles.SalesPerson, Roles.Installer, Roles.SuperAdmin]}
    >
      <Header />

      <NavigationSheet />
      <PostHogPageView />
      <OnboardingTrigger />
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <Outlet />
      </React.Suspense>
    </RoleProtectedRoute>
  );
};

const SuperAdminRoutes = () => {
  return (
    <RoleProtectedRoute roles={[Roles.SuperAdmin]}>
      <Header />
      <NavigationSheet />
      <PostHogPageView />
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <Outlet />
      </React.Suspense>
    </RoleProtectedRoute>
  );
};

const BillingRoutes = () => {
  return (
    <FeatureFlagged name={FeatureFlags.BillingPage}>
      <RoleProtectedRoute roles={[Roles.SuperAdmin, Roles.IntegratorAdmin]}>
        <Header />
        <NavigationSheet />
        <PostHogPageView />
        <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
          <Outlet />
        </React.Suspense>
      </RoleProtectedRoute>
    </FeatureFlagged>
  );
};

const SharedRoutes = () => {
  return (
    <RoleProtectedRoute>
      <Header />

      <NavigationSheet />
      <PostHogPageView />
      <OnboardingTrigger />
      <AnnouncementAutoDismiss />
      <WhatsNewModal />
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <Outlet />
      </React.Suspense>
    </RoleProtectedRoute>
  );
};

const unAuthRoutes: TitledRoute[] = [
  { path: '/error', title: 'Error', element: <ErrorPage /> },
  { path: '/login', title: 'Login', element: <Login /> },
  { path: '/login/2fa', title: 'Two-Factor Authentication', element: <TwoFactorPage /> },
  { path: '/login/redeem/:token', title: 'Redeem Token', element: <RedeemTokenPage /> },
  { path: '/monitoring/login/:token', title: 'Monitoring Login', element: <OneLink /> },
  { path: '/forgotpassword', title: 'Forgot Password', element: <ForgotPassword /> },
  {
    path: '/reset/:rawToken',
    title: 'Reset Password',
    element: <CreatePassword userFlow="reset" />,
  },
  {
    path: '/welcome/:rawToken',
    title: 'Welcome',
    element: <CreatePassword userFlow="welcome" />,
  },
  {
    path: '/welcome-go/:rawToken',
    title: 'Welcome to Watchful Go',
    element: <WelcomeGo />,
  },
  {
    path: '/download-go',
    title: 'Download Watchful Go',
    element: <DownloadGo />,
  },
  {
    path: '/forgot-password-go',
    title: 'Forgot Password',
    element: <ForgotPasswordGo />,
  },
  {
    path: '/reset-go/:rawToken',
    title: 'Reset Password',
    element: <ResetPasswordGo />,
  },
  {
    path: '/alert-login',
    title: 'Alert Login',
    element: <AlertLogin />,
  },
  {
    path: '/promo-terms',
    title: 'Promotional Terms',
    element: <PromoTerms />,
  },
  {
    path: '/edgelink/s/:token',
    title: 'EdgeLink Status',
    element: (
      <React.Suspense fallback={<div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-500" /></div>}>
        <EdgeLinkSharedStatusPage />
      </React.Suspense>
    ),
  },
];

const sharedRoutes: TitledRoute[] = [
  { path: '/home', title: 'Home', element: <Home /> },
  { path: '/dashboard', title: 'Dashboard', element: <Home /> },
  { path: '/', title: 'Home', element: <Home /> },
  { path: '/whats-new', title: "What's New", element: <WhatsNewPage /> },
];

const monitoringNoLayoutRoutes: TitledRoute[] = [
  {
    path: '/alert/live',
    title: 'Live Alert View',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <AlertLiveViewSidecar />
      </React.Suspense>
    ),
  },
  {
    path: '/kanban-tv',
    title: 'Kanban Dashboard',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <KanbanTVDashboard />
      </React.Suspense>
    ),
  },
];

const monitoringRoutes: TitledRoute[] = [
  {
    path: '/monitor',
    title: 'Monitoring',
    element: (
      <RequireShift>
        <App />
      </RequireShift>
    ),
  },
  {
    path: '/monitoring-overview',
    title: 'Monitoring Overview',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <MonitoringOverview />
      </React.Suspense>
    ),
  },
  {
    path: '/monitoring-overview-v2',
    title: 'Monitoring Overview',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <MonitoringOverviewV2 />
      </React.Suspense>
    ),
  },
  {
    path: '/alert/:alertId',
    title: 'Alert',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <RequireShift>
          <Alert />
        </RequireShift>
      </React.Suspense>
    ),
  },
  {
    path: '/alert-audit',
    title: 'Alert Audit',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <AlertAuditPage />
      </React.Suspense>
    ),
  },
];

const adminRoutes: TitledRoute[] = [
  { path: '/sites', title: 'Sites', element: <Sites /> },
  { path: '/site/:siteId', title: 'Site Details', element: <SiteDetails /> },
  {
    path: '/Customer/:companyId/project/:siteId',
    title: 'Site Details',
    element: <SiteDetails />,
  },
  { path: '/Customer/:companyId/site/new', title: 'Add Site', element: <SiteAddNew /> },
  { path: '/site/new', title: 'Add Site', element: <SiteAddNew /> },
  { path: '/site/new/:customerId', title: 'Add Site', element: <SiteAddNew /> },
  { path: '/viewer', title: 'Viewer', element: <Viewer2 /> },
  { path: '/customers', title: 'Customers', element: <CustomersPage /> },
  { path: '/alerts', title: 'Alerts', element: <AdminAlerts /> },
  {
    path: '/customers/:customerId',
    title: 'Customer Details',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <CustomerDetailV2 />
      </React.Suspense>
    ),
  },
  { path: '/monitoring/alert/:alertId', title: 'Alert', element: <AdminAlert /> },
  {
    path: '/monitoring/alert/:alertId/report',
    title: 'Alert Report',
    element: <AlertReportPage />,
  },
  { path: '/devices/customer/:customerID/add-device', title: 'Add Device', element: <AddDevice /> },
  { path: '/devices/add-device', title: 'Add Device', element: <DeviceAddNew /> },
  { path: '/devices', title: 'Devices', element: <Devices /> },
  { path: '/devices/:id/edit', title: 'Device', element: <DevicePage /> },
  { path: '/devices/:deviceId/camera/:cameraId', title: 'Camera', element: <CameraPage /> },
  { path: '/connections', title: 'Connections', element: <ExternalConnections /> },
  { path: '/connections/add-connection', title: 'Add Connection', element: <AddConnection /> },
  { path: '/connections/:connectionId', title: 'Edit Connection', element: <EditConnection /> },
  { path: '/users', title: 'Users', element: <UsersPage /> },
  { path: '/team', title: 'Teams', element: <Teamlist /> },
  { path: '/team/add', title: 'Add Team', element: <AddTeamPage /> },
  { path: '/tenant/:tenantId/team/:teamId', title: 'Team Details', element: <TeamDetailPage /> },
  { path: '/users/add', title: 'Add User', element: <AddUser /> },
  { path: '/users/:userId', title: 'Edit User', element: <EditUsersPageSimple /> },
  { path: '/sidecar', title: 'Sidecar', element: <Sidecar /> },
  { path: '/sales', title: 'Sales', element: <SalesPage /> },
  { path: '/products', title: 'Products', element: <ProductsPage /> },
  { path: '/users', title: 'Users', element: <UsersPage /> },
  { path: '/users/add', title: 'Add User', element: <EditUsersPage /> },
  { path: '/users/:userId', title: 'Edit User', element: <EditUsersPage /> },
  { path: '/health', title: 'Health', element: <Health /> },
  { path: '/system-health', title: 'System Health', element: <div>System Health</div> },
  { path: '/patrol', title: 'Patrols', element: <PatrolsPage /> },
  { path: '/patrol/add', title: 'Add Patrol', element: <AddPatrolPage /> },
  {
    path: '/edgelink',
    title: 'EdgeLink',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <EdgeLinkPage />
      </React.Suspense>
    ),
  },
  {
    path: '/edgelink/add',
    title: 'Add EdgeLink',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <AddEdgeLinkPage />
      </React.Suspense>
    ),
  },
  {
    path: '/edgelink/:id',
    title: 'EdgeLink Details',
    element: (
      <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
        <EdgeLinkDetailPage />
      </React.Suspense>
    ),
  },
  { path: '/reporting', title: 'Reporting', element: <ReportingPage /> },
];

const billingRoutes: TitledRoute[] = [
  { path: '/billing', title: 'Billing', element: <BillingPage /> },
];

const superAdminRoutes: TitledRoute[] = [
  { path: '/ihub', title: 'Integration Hub', element: <IntegrationHub /> },
  { path: '/ihub/integration/:id', title: 'Integration', element: <IntegrationPage /> },
  { path: '/ihub/monitor', title: 'Polling Monitor', element: <PollingMonitor /> },
  { path: '/system', title: 'System', element: <SystemPage /> },
  { path: '/ingestion', title: 'Ingestion', element: <SystemPage /> },
  { path: '/ingestion/legacy', title: 'Inbound Webhooks', element: <Inbound /> },
  { path: '/testing', title: 'Prompt Tester', element: <PromptTester /> },
  {
    path: '/edgelink-deployments',
    title: 'EdgeLink Deployments',
    element: <EdgeLinkDeploymentsPage />,
  },
];

// Update the router configuration to use the Root component
const router = createBrowserRouter([
  ...withTitles(unAuthRoutes),
  // Alert routes with separate authentication handling
  {
    path: '/review/:alertId',
    element: (
      <CustomerRouteProtection allowCustomerUsers={true} redirectTo="/alert-login">
        <PageTitle title="Alert Review" />
        <PostHogPageView />
        <React.Suspense fallback={<Loader2 className="h-8 w-8 animate-spin text-gray-500" />}>
          <AlertViewerWithLogin />
        </React.Suspense>
      </CustomerRouteProtection>
    ),
  },
  // Shared routes first (contains basic routes like /, /home, /dashboard)
  {
    path: '/',
    element: <SharedRoutes />,
    children: withTitles(sharedRoutes),
  },
  // More specific routes after
  {
    path: '/',
    element: <MonitoringRoutes />,
    children: withTitles(monitoringRoutes),
  },
  {
    path: '/',
    element: <MonitoringRoutesNoLayout />,
    children: withTitles(monitoringNoLayoutRoutes),
  },
  {
    path: '/',
    element: <AdminRoutes />,
    children: withTitles(adminRoutes),
  },
  {
    path: '/',
    element: <SuperAdminRoutes />,
    children: withTitles(superAdminRoutes),
  },
  {
    path: '/',
    element: <BillingRoutes />,
    children: withTitles(billingRoutes),
  },

  // 404 catch-all route
  {
    path: '*',
    element: (
      <>
        <PageTitle title="Not Found" />
        <NotFoundPage />
      </>
    ),
  },
]);

// Update the root render to remove PostHogPageView from outside RouterProvider
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PHProvider>
      <QueryClientProvider client={queryClient}>
        <FrontChatProvider>
          <HotkeyProvider initialMode="keyboard">
            <GamepadModeHandler>
              <MultiWindowProvider>
                <ErrorBoundary>
                  <TooltipProvider>
                    <NuqsAdapter>
                      <RouterProvider router={router} />
                    </NuqsAdapter>
                    <ShiftTimeoutListeners />
                    <IdleShiftTerminator />
                    <ShiftBroadcastListener />
                    <ShiftBreachToaster />
                    <OnboardingDevTools />
                  </TooltipProvider>
                  <DisconnectedOverlay />
                </ErrorBoundary>
              </MultiWindowProvider>
            </GamepadModeHandler>
          </HotkeyProvider>
        </FrontChatProvider>
        <Toaster />
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </PHProvider>
  </React.StrictMode>
);

// PostHogUserIdentifier has been moved to providers.tsx

export function GamepadModeHandler({ children }: { children: React.ReactNode }) {
  const { setMode } = useHotkeyMode();

  React.useEffect(() => {
    const handleGamepadConnected = () => {
      setMode('controller');
    };

    const handleGamepadDisconnected = () => {
      setMode('keyboard');
    };

    // Check for existing gamepads when component mounts
    const gamepads = navigator.getGamepads();
    for (const gamepad of gamepads) {
      if (gamepad) {
        setMode('controller');
        break;
      }
    }

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    };
  }, [setMode]);

  return <>{children}</>;
}
